/**
 * Import-lists dialog.
 *
 * Presents a textarea for the user to paste JSON produced by the
 * Export flow (typically received via SMS/email from another user).
 * The Import button is disabled until the textarea has content so
 * a stray tap can't no-op with an empty paste.
 *
 * ESC and overlay click call onCancel. Tab wraps between the three
 * focusables: textarea, Cancel, Import. Initial focus lands on the
 * textarea so the user can paste immediately.
 *
 * Signature:
 *   showImportDialog({
 *     onConfirm: (json: string) => void,
 *     onCancel?: () => void
 *   })
 */

export function showImportDialog(opts) {
  const previouslyFocused = document.activeElement;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog modal-dialog--import" role="dialog" aria-modal="true"
         aria-labelledby="import-heading">
      <h2 id="import-heading" class="modal-dialog__heading">Import lists</h2>
      <p class="modal-dialog__message">
        Paste the JSON you received. Existing lists with the same id are
        replaced only when the incoming version is newer.
      </p>
      <label class="modal-dialog__field">
        <span class="visually-hidden">Lists JSON</span>
        <textarea class="import-dialog__textarea" rows="8"
                  aria-label="Lists JSON"
                  placeholder='[{"id":"...","name":"...","items":[...]}]'></textarea>
      </label>
      <div class="modal-dialog__actions">
        <button type="button" class="modal-btn modal-btn--cancel" data-action="cancel">
          Cancel
        </button>
        <button type="button" class="modal-btn modal-btn--confirm" data-action="import" disabled>
          Import
        </button>
      </div>
    </div>
  `;

  const textarea = overlay.querySelector('textarea');
  const cancelBtn = overlay.querySelector('[data-action="cancel"]');
  const importBtn = overlay.querySelector('[data-action="import"]');

  function close() {
    document.removeEventListener('keydown', onKeyDown, true);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try { previouslyFocused.focus(); } catch (_e) { /* no-op */ }
    }
  }

  function handleCancel() {
    close();
    if (typeof opts.onCancel === 'function') {
      try { opts.onCancel(); } catch (_e) { /* caller bug */ }
    }
  }

  function handleImport() {
    const json = textarea.value;
    close();
    try { opts.onConfirm(json); } catch (_e) { /* caller bug */ }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
      return;
    }
    if (e.key === 'Tab') {
      const order = [textarea, cancelBtn, importBtn].filter(el => !el.disabled);
      const first = order[0];
      const last = order[order.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  textarea.addEventListener('input', () => {
    importBtn.disabled = textarea.value.trim().length === 0;
  });

  cancelBtn.addEventListener('click', handleCancel);
  importBtn.addEventListener('click', handleImport);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) handleCancel();
  });
  document.addEventListener('keydown', onKeyDown, true);

  document.body.appendChild(overlay);
  textarea.focus();
}
